import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { FormBuilder, Validators } from '@angular/forms';

import { SlaRuleService, SlaRulePayload } from 'src/app/services/sla-rule.service';
import { SlaRule } from 'src/models/sla-rule';

import { SlaServiceTypeEnum } from 'src/enumerators/sla-service-type.enum';
import { SlaPriorityEnum } from 'src/enumerators/sla-priority.enum';
import { SlaTimeUnitEnum } from 'src/enumerators/sla-time-unit.enum';

type SlaScope = 'workspace' | 'contract';

export type SlaRuleDraft = SlaRulePayload & { id: string };

type SlaRuleLike = SlaRule | SlaRuleDraft;

@Component({
  selector: 'app-sla-rules',
  templateUrl: './sla-rules.component.html',
  styleUrls: ['./sla-rules.component.css'],
})
export class SlaRulesComponent implements OnChanges {
  @Input() scope: SlaScope = 'workspace';
  @Input() contractId?: string;

  @Input() draft: SlaRuleDraft[] = [];
  @Output() draftChange = new EventEmitter<SlaRuleDraft[]>();

  @ViewChild('confirmRemoveTpl') confirmRemoveTpl!: TemplateRef<any>;

  loading = false;
  rules: SlaRuleLike[] = [];

  formOpen = false;
  editingId?: string;

  modalRef?: BsModalRef;
  rulePendingDelete?: SlaRuleLike;

  duplicateError = false;

  // Ver mais / Ver menos
  showAll = false;
  initialDisplayCount = 4;

  serviceTypes = Object.values(SlaServiceTypeEnum);
  priorities = Object.values(SlaPriorityEnum);
  timeUnits = Object.values(SlaTimeUnitEnum);

  form = this.fb.group({
    serviceType: ['', [Validators.required]],
    priority: ['', [Validators.required]],
    responseTimeValue: [1, [Validators.required, Validators.min(1)]],
    responseTimeUnit: ['', [Validators.required]],
    resolutionTimeValue: [1, [Validators.required, Validators.min(1)]],
    resolutionTimeUnit: ['', [Validators.required]],
  });

  constructor(
    private fb: FormBuilder,
    private slaService: SlaRuleService,
    private modalService: BsModalService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['scope'] || changes['contractId']) {
      this.load();
      return;
    }

    if (changes['draft'] && this.isDraftMode) {
      this.rules = [...(this.draft ?? [])];
    }
  }

  get isDraftMode(): boolean {
    return this.scope === 'contract' && !this.contractId;
  }

  get title(): string {
    if (this.scope === 'workspace') return 'Regra de SLA Padrão';
    return 'Regras do Contrato';
  }

  get f() {
    return this.form.controls;
  }

  get displayedRules(): SlaRuleLike[] {
    if (this.showAll || this.rules.length <= this.initialDisplayCount) {
      return this.rules;
    }
    return this.rules.slice(0, this.initialDisplayCount);
  }

  toggleShowAll(): void {
    this.showAll = !this.showAll;
  }

  isInvalid(name: keyof typeof this.form.controls): boolean {
    const c = this.form.get(name as string);
    return !!c && c.invalid && (c.touched || c.dirty);
  }

  errorMessage(name: keyof typeof this.form.controls): string {
    const c = this.form.get(name as string);
    if (!c || !c.errors) return '';

    if (c.errors['required']) return 'Campo obrigatório.';
    if (c.errors['min']) return `O valor mínimo é ${c.errors['min'].min}.`;

    return 'Valor inválido.';
  }

  private checkDuplicate(): boolean {
    const serviceType = this.norm(this.form.value.serviceType);
    const priority = this.norm(this.form.value.priority);

    if (!serviceType || !priority) return false;

    const duplicate = this.rules.find((r) => {
      if (this.editingId && (r as any).id === this.editingId) {
        return false;
      }
      return (
        this.norm((r as any).serviceType) === serviceType &&
        this.norm((r as any).priority) === priority
      );
    });

    return !!duplicate;
  }

  private load() {
    if (this.isDraftMode) {
      this.loading = false;
      this.rules = [...(this.draft ?? [])];
      return;
    }

    this.loading = true;

    const req$ =
      this.scope === 'workspace'
        ? this.slaService.listWorkspaceDefaults()
        : this.slaService.listContractRules(this.contractId!);

    req$.subscribe({
      next: (rules) => {
        this.rules = (rules || []) as SlaRuleLike[];
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  openCreate() {
    this.editingId = undefined;
    this.duplicateError = false;
    this.form.reset({
      serviceType: '',
      priority: '',
      responseTimeValue: 1,
      responseTimeUnit: '',
      resolutionTimeValue: 1,
      resolutionTimeUnit: '',
    });
    this.formOpen = true;
  }

  openEdit(rule: SlaRuleLike) {
    this.editingId = rule.id;
    this.duplicateError = false;

    this.form.patchValue({
      serviceType: rule.serviceType,
      priority: rule.priority,
      responseTimeValue: rule.responseTimeValue,
      responseTimeUnit: rule.responseTimeUnit,
      resolutionTimeValue: rule.resolutionTimeValue,
      resolutionTimeUnit: rule.resolutionTimeUnit,
    });

    this.formOpen = true;
  }

  cancel() {
    this.formOpen = false;
    this.editingId = undefined;
    this.duplicateError = false;
  }

  clearForm() {
    this.duplicateError = false;
    this.form.reset({
      serviceType: '',
      priority: '',
      responseTimeValue: 1,
      responseTimeUnit: '',
      resolutionTimeValue: 1,
      resolutionTimeUnit: '',
    });
    this.form.markAsUntouched();
    this.form.markAsPristine();
  }

  onCombinationChange() {
    this.duplicateError = this.checkDuplicate();
  }

  save() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    if (this.checkDuplicate()) {
      this.duplicateError = true;
      return;
    }

    this.duplicateError = false;

    const payload: SlaRulePayload = {
      serviceType: this.form.value.serviceType!,
      priority: this.form.value.priority!,
      responseTimeValue: Number(this.form.value.responseTimeValue),
      responseTimeUnit: this.form.value.responseTimeUnit!,
      resolutionTimeValue: Number(this.form.value.resolutionTimeValue),
      resolutionTimeUnit: this.form.value.resolutionTimeUnit!,
    };

    if (this.isDraftMode) {
      const id = this.editingId ?? this.newDraftId();
      const nextRule: SlaRuleDraft = { id, ...payload };
      const next = this.editingId
        ? this.rules.map((r) => ((r as any).id === this.editingId ? nextRule : (r as any)))
        : [...this.rules, nextRule];

      this.rules = next;
      this.formOpen = false;
      this.editingId = undefined;
      this.emitDraft();
      return;
    }

    this.loading = true;

    const req$ =
      this.scope === 'workspace'
        ? this.editingId
          ? this.slaService.updateWorkspaceDefault(this.editingId, payload)
          : this.slaService.createWorkspaceDefault(payload)
        : this.editingId
          ? this.slaService.updateContractRule(this.contractId!, this.editingId, payload)
          : this.slaService.createContractRule(this.contractId!, payload);

    req$.subscribe({
      next: () => {
        this.loading = false;
        this.formOpen = false;
        this.editingId = undefined;
        this.load();
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  openRemoveModal(rule: SlaRuleLike) {
    this.rulePendingDelete = rule;
    this.modalRef = this.modalService.show(this.confirmRemoveTpl, {
      class: 'modal-dialog-centered modal-lg',
      backdrop: 'static',
      keyboard: false,
    });
  }

  closeRemoveModal() {
    this.modalRef?.hide();
    this.modalRef = undefined;
    this.rulePendingDelete = undefined;
  }

  confirmRemove() {
    if (!this.rulePendingDelete) return;

    if (this.isDraftMode) {
      const id = (this.rulePendingDelete as any).id;
      this.rules = this.rules.filter((r) => (r as any).id !== id);
      this.closeRemoveModal();
      this.emitDraft();
      return;
    }

    this.loading = true;

    const req$ =
      this.scope === 'workspace'
        ? this.slaService.deleteWorkspaceDefault((this.rulePendingDelete as any).id)
        : this.slaService.deleteContractRule(this.contractId!, (this.rulePendingDelete as any).id);

    req$.subscribe({
      next: () => {
        this.loading = false;
        this.closeRemoveModal();
        this.load();
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  trackById(_: number, item: SlaRuleLike) {
    return (item as any).id;
  }

  private emitDraft() {
    this.draftChange.emit([...(this.rules as SlaRuleDraft[])]);
  }

  private newDraftId(): string {
    try {
      if (crypto?.randomUUID) return crypto.randomUUID();
    } catch {}
    return `draft_${Date.now()}_${Math.random().toString(16).slice(2)}`;
  }

  norm(v: any): string {
    return String(v ?? '').trim().toUpperCase();
  }

  private readonly SERVICE_TYPE_PT: Record<string, string> = {
    [SlaServiceTypeEnum.maintenance]: 'Manutenção',
    [SlaServiceTypeEnum.implantation]: 'Implantação',
    [SlaServiceTypeEnum.replacement]: 'Substituição',
  };

  private readonly PRIORITY_PT: Record<string, string> = {
    [SlaPriorityEnum.high]: 'Alta',
    [SlaPriorityEnum.medium]: 'Média',
    [SlaPriorityEnum.low]: 'Baixa',
  };

  private readonly TIME_UNIT_PT: Record<string, { singular: string; plural: string }> = {
    MINUTE: { singular: 'minuto', plural: 'minutos' },
    MINUTES: { singular: 'minuto', plural: 'minutos' },
    HOUR: { singular: 'hora', plural: 'horas' },
    HOURS: { singular: 'hora', plural: 'horas' },
    DAY: { singular: 'dia', plural: 'dias' },
    DAYS: { singular: 'dia', plural: 'dias' },
  };

  serviceTypeLabel(v: any): string {
    const key = this.norm(v);
    return this.SERVICE_TYPE_PT[key] ?? String(v);
  }

  priorityLabel(v: any): string {
    const key = this.norm(v);
    return this.PRIORITY_PT[key] ?? String(v);
  }

  timeUnitLabel(unit: any, qty?: number): string {
    const key = this.norm(unit);
    const map = this.TIME_UNIT_PT[key];
    if (!map) return String(unit);
    const n = Number(qty ?? 2);
    return n === 1 ? map.singular : map.plural;
  }
}
